import ErrorSummary from '@/components/ErrorSummary'
import RuianAddressSelector from '@/components/RuianAddressSelector'
import { DashboardHeader } from '@/containers/dashboard/DashboardHeader'
import ICOOptions from '@/containers/forms/person/ICOOptions'
import { PersonID } from '@/containers/forms/person/PersonID'
import PersonType from '@/containers/forms/person/PersonType'
import { useCreateDummyPerson, useGetDummyPerson, useUpdateDummyPerson } from '@/hooks/useDummyPersons'
import { ArrowLeft } from '@/icons'
import { PageWrapper } from '@/layouts/components/PageWrapper'
import { Box, Button, Grid, Skeleton, Stack, useToast } from '@chakra-ui/react'
import { formatDate } from 'BrokerTsLib/src/Utils/formatPrice'
import { CheckboxFormControl, InputFormControl } from 'BrokerTsLib/src/components'
import { Breadcrumbs } from 'BrokerTsLib/src/containers/Breadcrumbs'
import { InitialPersonAdditionalData, type PersonAdditionalData } from 'BrokerTsLib/src/dtos'
import { Selection } from 'BrokerTsLib/src/enums/Selection'
import { FormProvider, useForm, yupResolver } from 'BrokerTsLib/src/rhf'
import { PersonValidationSchema } from 'BrokerTsLib/src/validation'
import * as global from 'brokertslib/src/helpers/global'
import { useRef } from 'react'
import { useNavigate, useParams } from 'react-router'
import * as yup from 'yup'

const schema = yup.object().shape(PersonValidationSchema) as any

const normalizePersonForForm = (p: PersonAdditionalData | undefined): PersonAdditionalData => {
  const base = p ?? InitialPersonAdditionalData
  return {
    ...base,
    name: base.name ?? '',
    surname: base.surname ?? '',
    birthNumber: base.birthNumber ?? '',
    idendificationNumber: base.idendificationNumber ?? '',
    companyName: base.companyName ?? '',
  }
}

export const DummyPersonEditPage = () => {
  const { id } = useParams<{ id: string }>()
  const personId = id ? Number(id) : undefined
  const isEdit = personId !== undefined

  const navigate = useNavigate()
  const toast = useToast()
  const validationSummaryRef = useRef<HTMLDivElement>(null)

  const { data: existingPerson, isLoading: isLoadingPerson } = useGetDummyPerson(personId)
  const { mutateAsync: createPerson, isPending: isCreating } = useCreateDummyPerson()
  const { mutateAsync: updatePerson, isPending: isUpdating } = useUpdateDummyPerson()
  const isSaving = isCreating || isUpdating

  const methods = useForm<PersonAdditionalData>({
    values: isEdit ? normalizePersonForForm(existingPerson) : InitialPersonAdditionalData,
    resolver: yupResolver(schema),
    mode: 'onBlur',
  })

  const { control, handleSubmit, watch, setError, formState: { errors } } = methods

  const type = watch('type')
  const isNaturalPerson = type === Selection.Person || type === Selection.SelfEmployed
  const isCompanyOrSelfEmployed = type === Selection.Company || type === Selection.SelfEmployed

  const scrollToValidationSummary = () => {
    setTimeout(() => {
      validationSummaryRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, 0)
  }

  const onSubmit = handleSubmit(
    async (data) => {
      try {
        data.birthday = formatDate(data.birthday)

        if (isEdit) {
          await updatePerson(data)
          toast({ title: 'Klient byl upraven', status: 'success', duration: 3000 })
        } else {
          await createPerson(data)
          toast({ title: 'Klient byl přidán', status: 'success', duration: 3000 })
        }
        navigate('/supervizor/dummy-persons')
      } catch (err: any) {
        if (err?.response?.status === 400 && err?.response?.data) {
          const data = err.response.data
          Object.keys(data).forEach((x: any) => {
            setError(global.toCamelCase(x), { message: data[x].join(' ') })
          })
          scrollToValidationSummary()
        } else {
          toast({ title: 'Nepodařilo se uložit klienta', status: 'error', duration: 4000 })
        }
      }
    },
    () => scrollToValidationSummary()
  )

  const pageTitle = isEdit ? 'Upravit dummy klienta' : 'Přidat dummy klienta'

  return (
    <PageWrapper
      header={
        <>
          <Breadcrumbs
            items={[
              { to: '/supervizor/dummy-persons', label: 'Dummy klienti' },
              { to: isEdit ? `/supervizor/dummy-persons/edit/${personId}` : '/supervizor/dummy-persons/create', label: pageTitle, current: true },
            ]}
          />
          <DashboardHeader sx={{ py: 16 }} title={pageTitle} />
        </>
      }
      mainProps={{ sx: { pt: { base: 0, md: 8 }, gap: 6, display: 'grid' } }}
    >
      <Box bg="white" p={6} borderRadius="lg" shadow="sm">
        <Skeleton isLoaded={!isLoadingPerson}>
          <FormProvider {...methods}>
            <Stack gap={8}>
              <PersonType personPrefix="" radioStackProps={{ spacing: 6 }} />

              {isCompanyOrSelfEmployed && (
                <CheckboxFormControl name="isVatPayer" control={control} label="Plátce DPH" />
              )}

              {isNaturalPerson && (
                <Box sx={{ '& > div': { columnGap: 6, rowGap: 6 } }}>
                  <PersonID personPrefix="" fullWidth />
                </Box>
              )}

              {isCompanyOrSelfEmployed && (
                <Box sx={{ '& > div': { columnGap: '12px !important', rowGap: '16px !important' } }}>
                  <ICOOptions personPrefix="" />
                </Box>
              )}

              {isNaturalPerson && (
                <Grid templateColumns="80px 1fr 1fr 80px" gap={6}>
                  <InputFormControl name="titleBefore" control={control} label="Titul" />
                  <InputFormControl name="name" control={control} label="Jméno" />
                  <InputFormControl name="surname" control={control} label="Příjmení" />
                  <InputFormControl name="titleAfter" control={control} label="Titul za" />
                </Grid>
              )}

              <Box sx={{ '& > div': { columnGap: 6, rowGap: 6 } }}>
                <RuianAddressSelector addressPath="address" showOnlyLotNumber={false} />
              </Box>

              <Grid templateColumns="1fr 1fr" gap={6}>
                <InputFormControl name="phone" control={control} label="Telefon" />
                <InputFormControl name="email" control={control} label="Email" />
              </Grid>

              <ErrorSummary errors={errors} ref={validationSummaryRef} />

              <Grid
                sx={{
                  width: '100%',
                  gridTemplateColumns: 'auto auto',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Button
                  variant="ghost"
                  type="button"
                  isDisabled={isSaving}
                  onClick={() => navigate('/supervizor/dummy-persons')}
                >
                  <ArrowLeft color="secondary.500" />
                  Zpět
                </Button>
                <Button
                  colorScheme="primary"
                  isLoading={isSaving}
                  onClick={onSubmit}
                >
                  {isEdit ? 'Uložit' : 'Přidat dummy klienta'}
                </Button>
              </Grid>
            </Stack>
          </FormProvider>
        </Skeleton>
      </Box>
    </PageWrapper>
  )
}
